import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { 
  Cake, ClipboardList, GraduationCap, Clock, CalendarDays,
  FileText, CheckCircle2, AlertCircle, User, BellRing, CalendarPlus
} from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";

const PortalColaborador = () => {
  const { user } = useAuth();
  const [birthdays, setBirthdays] = useState([]);
  const [tasks, setTasks] = useState({ onboarding_tasks: [], training_assignments: [], pending_approvals: [] });
  const [announcements, setAnnouncements] = useState([]);
  const [notificationPrefs, setNotificationPrefs] = useState({ email_opt_in: true, whatsapp_opt_in: false, internal_chat_opt_in: true, calendly_connected: false, calendly_username: "", preferred_channels: ["internal_chat"] });
  const [internalNotifications, setInternalNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const opts = { headers, withCredentials: true };
      const [bRes, tRes, aRes, prefsRes, notifRes] = await Promise.all([
        axios.get(`${API}/portal/birthdays`, opts).catch(() => ({ data: [] })),
        axios.get(`${API}/portal/my-tasks`, opts).catch(() => ({ data: { onboarding_tasks: [], training_assignments: [], pending_approvals: [] } })),
        axios.get(`${API}/announcements`, opts).catch(() => ({ data: [] })),
        axios.get(`${API}/user/notification-consent`, opts).catch(() => ({ data: null })),
        axios.get(`${API}/internal-notifications`, opts).catch(() => ({ data: [] })),
      ]);
      setBirthdays(bRes.data || []);
      setTasks(tRes.data || { onboarding_tasks: [], training_assignments: [], pending_approvals: [] });
      setAnnouncements(aRes.data || []);
      if (prefsRes.data) setNotificationPrefs(prefsRes.data);
      setInternalNotifications(notifRes.data || []);
    } catch {
      toast.error("Error al cargar portal");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <Layout title="Portal del Colaborador">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  const totalTasks = (tasks?.onboarding_tasks?.length || 0) + (tasks?.training_assignments?.length || 0) + (tasks?.pending_approvals?.length || 0);

  const savePreferences = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/user/notification-consent`, notificationPrefs, { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true });
      toast.success("Preferencias guardadas");
    } catch {
      toast.error("No se pudieron guardar las preferencias");
    }
  };

  const connectCalendly = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/oauth/calendly/connect/start`, {}, { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true });
      if (response.data.status === "ready" && response.data.authorization_url) {
        window.open(response.data.authorization_url, "_blank");
      } else {
        toast.message(`Calendly: ${response.data.status}`);
      }
    } catch {
      toast.error("No se pudo iniciar Calendly");
    }
  };

  return (
    <Layout title="Portal del Colaborador" subtitle={`Bienvenido, ${user?.name || "Colaborador"}`}>
      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="portal-stats">
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <ClipboardList className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{totalTasks}</p>
              <p className="text-sm text-slate-500">Tareas pendientes</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-pink-100 flex items-center justify-center">
              <Cake className="w-5 h-5 text-pink-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{birthdays.length}</p>
              <p className="text-sm text-slate-500">Cumpleaños próximos</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{tasks?.training_assignments?.length || 0}</p>
              <p className="text-sm text-slate-500">Capacitaciones asignadas</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <FileText className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{announcements.length}</p>
              <p className="text-sm text-slate-500">Anuncios activos</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* My Tasks */}
        <div className="lg:col-span-2 space-y-6">
          {/* Onboarding Tasks */}
          {(tasks?.onboarding_tasks?.length || 0) > 0 && (
            <Card data-testid="onboarding-tasks-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <ClipboardList className="w-4 h-4 text-emerald-600" />
                  Tareas de Onboarding
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {(tasks?.onboarding_tasks || []).map((task, i) => (
                  <div key={task.task_id || i} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
                    <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">{task.title || task.name || "Tarea"}</p>
                      {task.description && <p className="text-xs text-slate-500 truncate">{task.description}</p>}
                    </div>
                    <Badge className="bg-amber-100 text-amber-700">{task.status}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Training Assignments */}
          {(tasks?.training_assignments?.length || 0) > 0 && (
            <Card data-testid="training-tasks-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <GraduationCap className="w-4 h-4 text-blue-600" />
                  Capacitaciones Asignadas
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {(tasks?.training_assignments || []).map((t, i) => (
                  <div key={t.assignment_id || i} className="p-3 rounded-lg bg-slate-50">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-slate-900">{t.training_id}</p>
                      <Badge className="bg-blue-100 text-blue-700">{t.status}</Badge>
                    </div>
                    <Progress value={t.progress || 0} className="h-2" />
                    <p className="text-xs text-slate-500 mt-1">{t.progress || 0}% completado</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Pending Approvals */}
          {(tasks?.pending_approvals?.length || 0) > 0 && (
            <Card data-testid="pending-approvals-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="w-4 h-4 text-violet-600" />
                  Solicitudes Pendientes
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {(tasks?.pending_approvals || []).map((req, i) => (
                  <div key={req.leave_id || i} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
                    <CalendarDays className="w-4 h-4 text-violet-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">{req.leave_type || "Permiso"}</p>
                      <p className="text-xs text-slate-500">{req.start_date} - {req.end_date}</p>
                    </div>
                    <Badge className="bg-amber-100 text-amber-700">Pendiente</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Empty state */}
          {totalTasks === 0 && (
            <Card data-testid="no-tasks-card">
              <CardContent className="p-8 text-center">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-400" />
                <p className="font-medium text-slate-700">No tienes tareas pendientes</p>
                <p className="text-sm text-slate-500 mt-1">Estás al día con todas tus actividades</p>
              </CardContent>
            </Card>
          )}

          {/* Announcements */}
          {announcements.length > 0 && (
            <Card data-testid="announcements-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-600" />
                  Anuncios
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {announcements.slice(0, 5).map((ann, i) => (
                  <div key={ann.announcement_id || i} className="p-3 rounded-lg bg-slate-50">
                    <p className="text-sm font-semibold text-slate-900">{ann.title}</p>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">{ann.content}</p>
                    <p className="text-xs text-slate-400 mt-1">{ann.published_at?.slice(0, 10)}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Birthdays Sidebar */}
        <div>
          <Card className="mb-6" data-testid="portal-notification-preferences-card">
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><BellRing className="w-4 h-4 text-sky-600" />Preferencias y agenda</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between"><span className="text-sm text-slate-700">Email</span><Switch checked={!!notificationPrefs.email_opt_in} onCheckedChange={value => setNotificationPrefs(previous => ({ ...previous, email_opt_in: value }))} data-testid="portal-email-optin-switch" /></div>
              <div className="flex items-center justify-between"><span className="text-sm text-slate-700">WhatsApp</span><Switch checked={!!notificationPrefs.whatsapp_opt_in} onCheckedChange={value => setNotificationPrefs(previous => ({ ...previous, whatsapp_opt_in: value }))} data-testid="portal-whatsapp-optin-switch" /></div>
              <div className="flex items-center justify-between"><span className="text-sm text-slate-700">Chat interno</span><Switch checked={!!notificationPrefs.internal_chat_opt_in} onCheckedChange={value => setNotificationPrefs(previous => ({ ...previous, internal_chat_opt_in: value }))} data-testid="portal-chat-optin-switch" /></div>
              <Button variant="outline" className="w-full" onClick={connectCalendly} data-testid="portal-calendly-connect-btn"><CalendarPlus className="w-4 h-4 mr-2" />Conectar Calendly</Button>
              <Button className="w-full bg-sky-600 hover:bg-sky-700" onClick={savePreferences} data-testid="portal-save-preferences-btn">Guardar preferencias</Button>
            </CardContent>
          </Card>

          {internalNotifications.length > 0 && <Card className="mb-6" data-testid="portal-internal-notifications-card"><CardHeader className="pb-3"><CardTitle className="text-base">Bandeja interna</CardTitle></CardHeader><CardContent className="space-y-3">{internalNotifications.slice(0, 5).map(notification => <div key={notification.notification_id} className="p-3 rounded-lg bg-slate-50"><p className="text-sm font-medium text-slate-900">{notification.title}</p><p className="text-xs text-slate-500 mt-1">{notification.body}</p></div>)}</CardContent></Card>}

          <Card data-testid="birthdays-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Cake className="w-4 h-4 text-pink-500" />
                Cumpleaños Próximos
              </CardTitle>
            </CardHeader>
            <CardContent>
              {birthdays.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No hay cumpleaños próximos</p>
              ) : (
                <div className="space-y-3">
                  {birthdays.map((b, i) => (
                    <div key={b.employee_id || i} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-pink-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{b.name}</p>
                        <p className="text-xs text-slate-500">{b.department}</p>
                      </div>
                      <Badge className={b.days_until === 0 ? "bg-pink-100 text-pink-700" : "bg-slate-100 text-slate-600"}>
                        {b.days_until === 0 ? "Hoy" : `${b.days_until}d`}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default PortalColaborador;
